import React, {
  FC, useCallback, useContext, useEffect,
} from 'react';
import {
  editGroupParticipants,
  GroupConversationMetadata,
  PublicConversationMetadata,
  selectMetadataById,
  selectTwilioUserEntities,
  selectMetadataOtherUserById,
  updateMetadata,
  UserReachabilityStatus,
} from 'models/twilio';
import {
  usePopupState,
  bindTrigger,
  bindPopover,
} from 'material-ui-popup-state/hooks';
import { GroupRounded } from '@material-ui/icons';
import { ButtonBase, Popover } from '@material-ui/core';
import { batch, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import TRANSLATIONS from 'components/twilio/translations';
import { useTranslation } from 'react-i18next';
import getMissingUsers from '../utils/getMissingUsers';
import MessageLoadingIndicator from './styled/MessageLoadingIndicator';
import ColoredMaterialIcon from './styled/ColoredMaterialIcon';
import { COLORS, POSITIONING } from '../constants';
import ChatIconButton from './styled/ChatIconButton';
import OnlineDot from './styled/OnlineDot';
import {
  ChatDisplayContainerStyles,
  ChatTitleSubtitleGroup,
} from './CommonChatDisplayComponents';
import UserAvatar from './styled/UserAvatar';
import useTwilioEventFunctions from '../hooks/useTwilioEventFunctions';
import ConverastionContext from '../ConversationContext';
import useMutableRef from '../hooks/useMutableRef';

const toInt = (str: string) => Number.parseInt(str.split('px')[0], 10);
const DEFAULT_POPPER_HEIGHT = toInt(POSITIONING.DESKTOP.INTERFACE_HEIGHT) - toInt(POSITIONING.HEADER_HEIGHT) - toInt(POSITIONING.TITLE_HEIGHT);

const StyledPopover = styled(Popover)`
  .MuiPopover-paper {
    @media screen and (max-width: 300px) {
      width: 100vw;
    }

    @media screen and (max-height: ${DEFAULT_POPPER_HEIGHT}px) {
      height: 75vh;
    }

    width: 300px;
    min-height: calc(42px + 57px);
    max-height: ${DEFAULT_POPPER_HEIGHT}px;
    height: fit-content;
    display: flex;
    flex-direction: column;
  }
`;

const StyledMessageLoadingIndicator = styled(MessageLoadingIndicator)`
  transform: translate(-50%, -50%);
`;

const GroupMemberItemContainer = styled(ButtonBase)`
  ${ChatDisplayContainerStyles}
  max-height: 56px;

  &:hover {
    background-color: ${COLORS.GREY_M};
  }
`;

const GroupMemberItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto;
`;

const GroupMemberTitle = styled.div`
  padding: 12px 16px;
  font-size: 14px;
  color: ${COLORS.TEXT_DARK};
  border-bottom: 1px solid ${COLORS.GREY_H};
`;

interface GroupMemberItemProps {
  id: string;
  name?: string;
  tag?: string;
  description?: string;
  reachabilityStatus: UserReachabilityStatus;
  createPrivateChat: (secondParticipantUuid: string) => Promise<void>;
}

const GroupMemberItem: FC<GroupMemberItemProps> = ({
  id, name, tag, description, createPrivateChat, reachabilityStatus,
}) => {
  const onClick = useCallback(() => createPrivateChat(id), [createPrivateChat, id]);
  return (
    <GroupMemberItemContainer onClick={onClick}>
      <UserAvatar marginRight={12} name={name} />
      <ChatTitleSubtitleGroup
        title={name || id}
        tag={tag}
        subtitle={description}
      />
      {reachabilityStatus === UserReachabilityStatus.ONLINE && <OnlineDot />}
    </GroupMemberItemContainer>
  );
};

export interface GroupMemberListProps {
  conversationSid: string;
  exhibitorOnly?: boolean;
}

export const GroupMemberList: FC<GroupMemberListProps> = ({ conversationSid, exhibitorOnly }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getConversationById } = useContext(ConverastionContext);
  const conversation = getConversationById(conversationSid);
  const metadata = useSelector(selectMetadataById(conversationSid)) as GroupConversationMetadata | PublicConversationMetadata;
  const userEntitiesRef = useMutableRef(useSelector(selectTwilioUserEntities));
  const { createPrivateChat } = useTwilioEventFunctions();
  const groupMemberData = useSelector(selectMetadataOtherUserById(conversationSid, exhibitorOnly));

  useEffect(() => {
    const getGroupUsers = async () => {
      const participants = await conversation.getParticipants();
      const participantIds = participants.map((participant) => participant.identity);
      await getMissingUsers(conversation, userEntitiesRef.current, participantIds);
      batch(() => {
        dispatch(editGroupParticipants({
          type: 'ADD',
          conversationId: conversation.sid,
          participantIds,
        }));
        dispatch(updateMetadata({
          id: conversation.sid,
          fetchedAllParticipants: true,
        } as Partial<GroupConversationMetadata | PublicConversationMetadata> & { id: string }));
      });
    };
    if (metadata?.fetchedAllParticipants !== true) getGroupUsers();
  }, [conversation, metadata?.fetchedAllParticipants, userEntitiesRef, dispatch]);

  return (
    <>
      {metadata.fetchedAllParticipants ? (
        <>
          <GroupMemberTitle>{t(...TRANSLATIONS.COMMON.ROOM_MEMBERS)}</GroupMemberTitle>
          <GroupMemberItemsContainer>
            {groupMemberData.map((memberData) => (
              <GroupMemberItem key={`GroupMemberItem-${memberData.id}`} createPrivateChat={createPrivateChat} {...memberData} />
            ))}
          </GroupMemberItemsContainer>
        </>
      ) : (
        <StyledMessageLoadingIndicator />
      )}
    </>
  );
};

const GroupMemberButton = ({ conversationSid }) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: `GroupMemberPopup-${conversationSid}`,
  });
  return (
    <>
      <ChatIconButton id="chat-group-member-button" {...bindTrigger(popupState)}>
        <ColoredMaterialIcon Icon={GroupRounded} />
      </ChatIconButton>
      <StyledPopover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <GroupMemberList conversationSid={conversationSid} />
      </StyledPopover>
    </>
  );
};

export default GroupMemberButton;
